import { createMachine, InterpreterFrom, StateFrom } from 'xstate'

import {
   loginMachineStates,
   loginMachineEvents,
   loginMachineTags,
   AUTH_MACHINE_ID
} from '../constants/loginConstants'

interface LoginMachineContext {
   errorMessage: string
   username: string
   email: string
   otp: string
   maskedPhoneNumber: string
}

type LoginMachineEvents =
   | {
        type: typeof requestEmailOtp
        email: string
        isAcceptedTnc?: boolean
        captchaToken?: string | null
     }
   | { type: typeof verifyEmailOtp; otp: string }
   | { type: typeof resendEmailOtp; email: string }
   | {
        type: typeof requestUsernameOtp
        username: string
        isAcceptedTnc?: boolean
        captchaToken?: string | null
     }
   | { type: typeof verifyUsernameOtp; otp: string }
   | { type: typeof resendUsernameOtp; username: string }
   | { type: typeof retryFailedApi }
   | { type: typeof goBack }
   | { type: typeof onChangeValue }
   | { type: typeof onChangeOTP }
   | { type: typeof clearErrorMessage }

const {
   loginState,
   otpState,
   requestEmailOtpState,
   requestEmailOtpFailedState,
   verifyEmailOtpState,
   verifyEmailOtpFailedState,
   resendEmailOtpState,
   resendEmailOtpFailedState,

   requestUsernameOtpState,
   requestUsernameOtpFailedState,
   verifyUsernameOtpState,
   verifyUsernameOtpFailedState,
   resendUsernameOtpState,
   resendUsernameOtpFailedState,
   loginSuccess
} = loginMachineStates

const {
   requestEmailOtp,
   verifyEmailOtp,
   resendEmailOtp,
   requestUsernameOtp,
   verifyUsernameOtp,
   resendUsernameOtp,
   retryFailedApi,
   goBack,
   onChangeValue,
   onChangeOTP,
   clearErrorMessage
} = loginMachineEvents

const {
   loginScreen,
   otpScreen,
   requestOtpLoading,
   verifyOtpLoading,
   resendOtpLoading,
   apiFailure
} = loginMachineTags

const loginMachine = () =>
   createMachine<LoginMachineContext, LoginMachineEvents>({
      predictableActionArguments: true,
      id: AUTH_MACHINE_ID,
      initial: loginState,
      context: {
         errorMessage: '',
         email: '',
         username: '',
         otp: '',
         maskedPhoneNumber: ''
      },
      on: {
         [loginMachineEvents.clearErrorMessage]: {
            actions: 'clearErrorMessageInContext'
         }
      },
      states: {
         [loginState]: {
            entry: ['clearErrorMessageInContext'],
            tags: [loginScreen],
            on: {
               [requestEmailOtp]: {
                  actions: ['assignEmailToContext'],
                  target: requestEmailOtpState
               },
               [requestUsernameOtp]: {
                  actions: ['assignUsernameToContext'],
                  target: requestUsernameOtpState
               }
            }
         },
         [requestUsernameOtpState]: {
            tags: [loginScreen, requestOtpLoading],
            invoke: {
               src: 'loginWithUsernameOtp',
               onDone: {
                  actions: [
                     'updateUsernameAndEmailAndMaskedPhoneNumberInContext'
                  ],
                  target: otpState
               },
               onError: {
                  actions: ['assignErrorMessageToContext'],
                  target: requestUsernameOtpFailedState
               }
            }
         },
         [requestUsernameOtpFailedState]: {
            tags: [loginScreen, apiFailure],
            on: {
               [requestUsernameOtp]: {
                  actions: [
                     'assignUsernameToContext',
                     'clearErrorMessageInContext'
                  ],
                  target: requestUsernameOtpState
               },
               [onChangeValue]: {
                  actions: ['clearErrorMessageInContext'],
                  target: loginState
               }
            }
         },
         [requestEmailOtpState]: {
            tags: [loginScreen, requestOtpLoading],
            invoke: {
               src: 'loginWithEmailOtp',
               onDone: {
                  actions: ['updateEmailAndMaskedPhoneNumberInContext'],
                  target: otpState
               },
               onError: {
                  actions: ['assignErrorMessageToContext'],
                  target: requestEmailOtpFailedState
               }
            }
         },
         [requestEmailOtpFailedState]: {
            tags: [loginScreen, apiFailure],
            on: {
               [requestEmailOtp]: {
                  actions: [
                     'assignEmailToContext',
                     'clearErrorMessageInContext'
                  ],
                  target: requestEmailOtpState
               },
               [onChangeValue]: {
                  actions: ['clearErrorMessageInContext'],
                  target: loginState
               }
            }
         },
         [otpState]: {
            tags: [otpScreen],
            on: {
               [verifyEmailOtp]: {
                  actions: ['assignOtpToContext'],
                  target: verifyEmailOtpState
               },
               [verifyUsernameOtp]: {
                  actions: ['assignOtpToContext'],
                  target: verifyUsernameOtpState
               },
               [goBack]: {
                  target: loginState
               },
               [resendEmailOtp]: {
                  target: resendEmailOtpState
               },
               [resendUsernameOtp]: {
                  target: resendUsernameOtpState
               }
            }
         },
         [verifyEmailOtpState]: {
            tags: [otpScreen, verifyOtpLoading],
            invoke: {
               src: 'verifyEmailOtp',
               onDone: {
                  actions: ['onLoginSuccess', 'navigateSuccessPage'],
                  target: loginSuccess
               },
               onError: {
                  actions: ['assignErrorMessageToContext'],
                  target: verifyEmailOtpFailedState
               }
            }
         },
         [verifyEmailOtpFailedState]: {
            tags: [otpScreen, apiFailure],
            on: {
               [verifyEmailOtp]: {
                  actions: ['assignOtpToContext', 'clearErrorMessageInContext'],
                  target: verifyEmailOtpState
               },
               [goBack]: {
                  target: loginState
               },
               [onChangeOTP]: {
                  actions: ['clearErrorMessageInContext'],
                  target: otpState
               },
               [resendEmailOtp]: {
                  actions: ['clearErrorMessageInContext'],
                  target: resendEmailOtpState
               }
            }
         },
         [resendEmailOtpState]: {
            tags: [otpScreen, resendOtpLoading],
            invoke: {
               src: 'resendEmailOtp',
               onDone: { actions: ['startTimer'], target: otpState },
               onError: {
                  actions: ['assignErrorMessageToContext'],
                  target: resendEmailOtpFailedState
               }
            }
         },
         [resendEmailOtpFailedState]: {
            tags: [otpScreen, apiFailure],
            on: {
               [resendEmailOtp]: {
                  actions: ['clearErrorMessageInContext'],
                  target: resendEmailOtpState
               }
            }
         },
         [verifyUsernameOtpState]: {
            tags: [otpScreen, verifyOtpLoading],
            invoke: {
               src: 'verifyUsernameOtp',
               onDone: {
                  actions: [
                     'onLoginWithUsernameSuccess',
                     'navigateSuccessPage'
                  ],
                  target: loginSuccess
               },
               onError: {
                  actions: ['assignErrorMessageToContext'],
                  target: verifyUsernameOtpFailedState
               }
            }
         },
         [verifyUsernameOtpFailedState]: {
            tags: [otpScreen, apiFailure],
            on: {
               [verifyUsernameOtp]: {
                  actions: ['assignOtpToContext', 'clearErrorMessageInContext'],
                  target: verifyUsernameOtpState
               },
               [goBack]: {
                  target: loginState
               },
               [onChangeOTP]: {
                  actions: ['clearErrorMessageInContext'],
                  target: otpState
               },
               [resendUsernameOtp]: {
                  actions: ['clearErrorMessageInContext'],
                  target: resendUsernameOtpState
               }
            }
         },
         [resendUsernameOtpState]: {
            tags: [otpScreen, resendOtpLoading],
            invoke: {
               src: 'resendUsernameOtp',
               onDone: { actions: ['startTimer'], target: otpState },
               onError: {
                  actions: ['assignErrorMessageToContext'],
                  target: resendUsernameOtpFailedState
               }
            }
         },
         [resendUsernameOtpFailedState]: {
            tags: [otpScreen, apiFailure],
            on: {
               [resendUsernameOtp]: {
                  actions: ['clearErrorMessageInContext'],
                  target: resendUsernameOtpState
               }
            }
         },
         [loginSuccess]: {
            type: 'final'
         }
      }
   })

export default loginMachine

export type LoginMachine = ReturnType<typeof loginMachine>

export type LoginMachineState = StateFrom<LoginMachine>
export type LoginMachineSend = InterpreterFrom<LoginMachine>['send']
